import { useEffect } from 'react';
import { Link } from 'react-router';
import { useCanonical } from '../../hooks/useCanonical.js';

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebApplication',
  name: 'RideHub',
  description:
    'サイクリストによるサイクリストのためのスポットガイド。近くの自転車ショップ・カフェ・休憩スポットをマップで探せます。',
  url: 'https://ridehub.shiget.net',
  applicationCategory: 'SportsApplication',
  operatingSystem: 'Web, iOS, Android',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'JPY',
  },
};

function SectionDivider({ color, flip = false }: { color: string; flip?: boolean }) {
  const clipPath = flip ? 'polygon(0 0, 100% 100%, 0 100%)' : 'polygon(0 100%, 100% 0, 100% 100%)';
  return (
    <div
      className="absolute bottom-0 left-0 right-0 pointer-events-none"
      style={{ height: 48, backgroundColor: color, clipPath }}
    />
  );
}

export function LandingView() {
  useCanonical('https://ridehub.shiget.net/landing');
  useEffect(() => {
    const originalTitle = document.title;
    document.title = 'RideHub | ロードバイク・サイクリスト向けスポット共有マップ';

    const metaDesc = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const originalDesc = metaDesc?.content ?? '';
    if (metaDesc) {
      metaDesc.content =
        'ロードバイク・サイクリスト向けのスポット共有マップ。自転車ショップ・補給カフェ・絶景スポット・道の駅など、サイクリストが選んだリアルな情報をマップで発見。チェックインやコメントでコミュニティの情報を更新しよう。';
    }

    const metaOgUrl = document.querySelector<HTMLMetaElement>('meta[property="og:url"]');
    const originalOgUrl = metaOgUrl?.content ?? '';
    if (metaOgUrl) metaOgUrl.content = 'https://ridehub.shiget.net/landing';

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => {
      document.title = originalTitle;
      if (metaDesc) metaDesc.content = originalDesc;
      if (metaOgUrl) metaOgUrl.content = originalOgUrl;
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen font-sans">
      {/* ヒーローセクション */}
      <section className="relative bg-gradient-to-b from-blue-900 via-indigo-900 to-blue-800 text-white px-6 pt-14 pb-16 flex flex-col items-center text-center">
        <img
          src="/favicon.svg"
          alt="RideHub"
          className="w-48 h-48 mb-6"
          style={{
            filter: 'brightness(0) invert(1)',
          }}
        />
        <h1 className="text-4xl font-bold leading-snug mb-4">
          サイクリングスポットを
          <br />
          みんなで共有
        </h1>
        <p className="text-blue-100 text-base font-medium leading-relaxed mb-8">
          サイクリストによる
          <br />
          サイクリストのための
          <br />
          自転車スポットガイドマップ
        </p>
        <Link
          to="/"
          className="bg-white hover:bg-gray-100 text-primary font-semibold px-8 py-3 rounded-full text-base mb-10 active:scale-95 transition-transform shadow-lg"
        >
          RideHubを開く →
        </Link>
        <div className="w-full max-w-xs mx-auto">
          <img
            src="/help/map.png"
            alt="RideHub マップ画面"
            className="w-full rounded-2xl shadow-2xl"
          />
        </div>
        <SectionDivider color="white" />
      </section>

      {/* 特徴セクション */}
      <section className="relative bg-white px-6 pt-12 pb-16">
        <h2 className="text-xl font-bold text-center text-gray-800 mb-8">
          RideHubのここがポイント
        </h2>
        <div className="grid grid-cols-2 gap-4 max-w-lg mx-auto">
          {[
            {
              icon: '🗺️',
              title: '現在地ですぐ検索',
              desc: '出先でアプリを開けば、近くのスポットがすぐ見つかる。',
            },
            {
              icon: '🚴',
              title: 'サイクリスト目線',
              desc: '自転車ショップ・補給カフェ・休憩場所などのおすすめスポットを簡単に共有。',
            },
            {
              icon: '👥',
              title: 'コミュニティ',
              desc: 'チェックイン・いいね・コメントでスポット情報を評価。',
            },
            {
              icon: '📱',
              title: 'かんたん導入',
              desc: 'インストール不要、ホーム画面に追加すればすぐに使えるブラウザアプリ。',
            },
          ].map((item) => (
            <div key={item.title} className="bg-gray-50 rounded-2xl p-4">
              <div className="text-2xl mb-2">{item.icon}</div>
              <h3 className="text-sm font-semibold text-gray-800 mb-1">{item.title}</h3>
              <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
        <SectionDivider color="#f9fafb" flip />
      </section>

      {/* 使い方ステップセクション */}
      <section className="relative bg-gray-50 px-6 pt-12 pb-16">
        <h2 className="text-xl font-bold text-center text-gray-800 mb-10">使用方法</h2>
        <div className="max-w-lg mx-auto flex flex-col gap-10">
          {[
            {
              step: 'STEP 1',
              title: 'マップでスポットを探す',
              desc: '現在地を中心に自転車ショップ・カフェ・駐輪場などのスポットがピン表示される。\nカテゴリでフィルターして目的のスポットを見つけよう。\n右上の検索アイコンから地名・住所でマップを移動することもできる。',
              img: '/help/map.png',
              alt: 'マップ画面',
            },
            {
              step: 'STEP 2',
              title: 'スポット詳細で確認して行き先決定',
              desc: 'チェックイン数・いいね・タグで評判をチェック。\nナビボタンで自転車ルート設定済みのGoogle Mapsが起動するので現地へ向かおう。',
              img: '/help/spot-detail.png',
              alt: 'スポット詳細画面',
            },
            {
              step: 'STEP 3',
              title: 'チェックインして情報を更新',
              desc: '訪問したらチェックインやコメントしよう。\nあなたの情報がコミュニティの役に立つ。',
              img: '/help/spot-comment.png',
              alt: 'コメント入力画面',
            },
            {
              step: 'STEP 4',
              title: '新しいスポットを登録する',
              desc: 'まだ載っていないお気に入りのスポットを登録しよう。\nあなたの発見がほかのサイクリストの助けになる。',
              img: '/help/spot-register.png',
              alt: 'スポット登録画面',
            },
          ].map((item) => (
            <div
              key={item.step}
              className="bg-blue-50 rounded-2xl p-5 flex flex-col items-center gap-4 text-center"
            >
              <div>
                <span className="inline-block text-xs font-bold text-accent bg-orange-100 px-2.5 py-0.5 rounded-full mb-2">
                  {item.step}
                </span>
                <h3 className="text-base font-semibold text-gray-800">{item.title}</h3>
                <p className="text-sm text-gray-500 mt-1 leading-relaxed whitespace-pre-line">
                  {item.desc}
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-md overflow-hidden w-[65%]">
                <img src={item.img} alt={item.alt} className="w-full" loading="lazy" />
              </div>
            </div>
          ))}
        </div>
        <SectionDivider color="#fffbeb" />
      </section>

      {/* なぜRideHubを作ったのかセクション */}
      <section className="relative bg-amber-50 px-6 pt-12 pb-16">
        <div className="max-w-lg mx-auto">
          <h2 className="text-xl font-bold text-center text-gray-800 mb-8">
            なぜ RideHub を作ったのか
          </h2>

          <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
            <p>
              ロードバイクを始めたばかりの頃、出かける前に目的地付近のスポットを一生懸命にネットやSNSで調べたり、いざ目的地付近についてもお目当てのスポットが見つからなくて探し回ったり。。。
            </p>
            <p>
              また、仲間と走れば「あそこの店がいいよ」「あそこにラックがあるよ」と盛り上がるのに、その貴重な情報はいつも仲間内の会話だけで終わってしまう。
            </p>
            <p className="font-semibold text-gray-800">
              「これって、すごくもったいないな」と思ったのが始まりでした。
            </p>
          </div>

          <div className="mt-6 bg-white rounded-2xl p-5 space-y-3 shadow-sm">
            <p className="text-sm font-semibold text-gray-800 mb-4">
              私が欲しかったのは、こんな場です。
            </p>
            {[
              {
                label: '集約',
                desc: 'ネットをあちこち探さなくても、情報がギュッと集まっている。',
              },
              {
                label: '手軽',
                desc: 'ふらっとポタリングにでかけたときに、さっと近隣のスポットを見つけて立ち寄れる。',
              },
              {
                label: 'リアル',
                desc: '広告サイトではない「実際のサイクリスト」が知ってる穴場や生の声がわかる。',
              },
            ].map((item) => (
              <div key={item.label} className="flex items-start gap-3">
                <span className="text-primary font-bold text-sm whitespace-nowrap">
                  「{item.label}」
                </span>
                <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 space-y-3 text-center">
            <p className="text-lg font-bold text-gray-800">「ないなら、つくってしまえ！」</p>
            <p className="text-sm text-gray-600 leading-relaxed">
              そんな勢いで生まれたのが、この RideHub です。
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              RideHubは、みんなでつくる「サイクリストの集合知」です。
              <br />
              あなたのみつけた「お気に入り」が、どこかの誰かの走りを支えるきっかけになるかもしれません。
            </p>
            <p className="text-base font-semibold text-primary pt-2">
              さあ、今日はどこへ行きましょうか？
            </p>
          </div>
        </div>
        <SectionDivider color="#111827" flip />
      </section>

      {/* CTAセクション */}
      <section className="bg-gray-900 text-white px-6 py-12 text-center">
        <h2 className="text-xl font-bold mb-2">いつものライドを、もっと快適に</h2>
        <p className="text-gray-400 text-sm mb-8">
          ホーム画面に追加すれば、アプリのように使えます。
        </p>
        <Link
          to="/"
          className="inline-block bg-primary hover:bg-primary-600 text-white font-semibold px-8 py-3 rounded-full text-base mb-10 active:scale-95 transition-transform"
        >
          RideHubを開く →
        </Link>

        <div className="max-w-lg mx-auto text-left">
          <h3 className="text-sm font-semibold mb-4 text-center text-gray-300">
            ホーム画面への追加方法
          </h3>
          <div className="flex flex-col gap-3">
            <div className="bg-gray-800 rounded-2xl p-4">
              <p className="text-xs font-semibold text-gray-300 mb-2">iPhone / iPad（Safari）</p>
              <ol className="text-xs text-gray-400 flex flex-col gap-1 list-decimal list-inside">
                <li>Safari でこのページを開く</li>
                <li>画面下の共有ボタン（□↑）をタップ</li>
                <li>「ホーム画面に追加」を選択</li>
                <li>「追加」をタップして完了</li>
              </ol>
            </div>
            <div className="bg-gray-800 rounded-2xl p-4">
              <p className="text-xs font-semibold text-gray-300 mb-2">Android（Chrome）</p>
              <ol className="text-xs text-gray-400 flex flex-col gap-1 list-decimal list-inside">
                <li>Chrome でこのページを開く</li>
                <li>右上のメニュー（⋮）をタップ</li>
                <li>「ホーム画面に追加」または「アプリをインストール」を選択</li>
                <li>「インストール」をタップして完了</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="flex gap-6 mt-10 justify-center">
          <Link to="/faq" className="text-xs text-gray-400 hover:text-gray-200">
            よくある質問
          </Link>
          <Link to="/help" className="text-xs text-gray-400 hover:text-gray-200">
            使い方ガイド
          </Link>
          <Link to="/release-notes" className="text-xs text-gray-400 hover:text-gray-200">
            リリースノート
          </Link>
        </div>
        <p className="text-xs text-gray-600 mt-2">© 2026 RideHub</p>
      </section>
    </div>
  );
}
